import type {
	DataRequestStatus,
	EmissionValueAnsweredUnit,
	EmissionValueUnit,
	ProductOrServiceCategory,
} from '@api-client/api'
import { InfoKeyValue } from '@components/InfoKeyValue'
import { ProductStatusChip } from '@components/StatusChip'
import { formatCalendarDate } from '@helpers/dates'
import { getIsTransportationService } from '@helpers/productOrService'
import { useTranslation } from 'react-i18next'

import * as S from './styles'

interface IProps {
	isCustomer: boolean
	name?: string
	productName: string
	productReference?: string
	productRequestComment?: string
	productReplyComment?: string
	productDescription?: string
	status?: DataRequestStatus
	reason?: string
	className?: string
	isTemplate?: boolean
	productOrServiceCategory?: ProductOrServiceCategory
	unit?: EmissionValueUnit | EmissionValueAnsweredUnit
	purchasingPeriodStart?: string
	purchasingPeriodEnd?: string
}

const DetailsHeader = (props: IProps) => {
	const { t } = useTranslation()
	const {
		isCustomer,
		name,
		productName,
		productReference,
		productReplyComment,
		productRequestComment,
		productDescription,
		status,
		reason,
		className,
		isTemplate = false,
		productOrServiceCategory,
		unit,
		purchasingPeriodStart,
		purchasingPeriodEnd,
	} = props

	const isTransportationService = getIsTransportationService(
		productOrServiceCategory,
		unit
	)

	const translationContext = isTransportationService
		? 'transportation'
		: isTemplate
			? 'template'
			: 'product'

	const productOrService = isTransportationService
		? t('word.service')
		: t('word.product')

	return (
		<S.DetailsContainer className={className}>
			<S.DetailsTitle>
				{t('productDetails.generalInformation', {
					context: translationContext,
				})}
			</S.DetailsTitle>
			<InfoKeyValue
				className="key-value-container"
				label={t('table.supplier')}
				value={name}
			/>
			<InfoKeyValue
				className="key-value-container"
				label={
					isTemplate
						? t('table.templateName')
						: t('table.productName', { context: translationContext })
				}
				value={productName}
			/>
			<InfoKeyValue
				className="key-value-container"
				label={t('table.referenceNumber')}
				value={productReference}
			/>
			{purchasingPeriodStart || purchasingPeriodEnd ? (
				<InfoKeyValue
					className="key-value-container"
					label={t('formField.purchasingPeriod')}
					value={`${purchasingPeriodStart ? formatCalendarDate(purchasingPeriodStart) : ''} → ${purchasingPeriodEnd ? formatCalendarDate(purchasingPeriodEnd) : ''}`}
				/>
			) : null}

			<InfoKeyValue
				className="key-value-container"
				label={
					isTemplate
						? t('formField.templateDescription')
						: t('formField.productDescription', {
								productOrService,
							})
				}
				value={productDescription}
			/>
			<InfoKeyValue
				className="key-value-container"
				label={t('formField.productRequestComment')}
				value={productRequestComment}
			/>
			<InfoKeyValue
				className="key-value-container"
				label={t('formField.productReplyComment', {
					context: `${productRequestComment ? 'answer_' : ''}${
						isCustomer ? 'asCustomer' : 'asSupplier'
					}`,
				})}
				value={productReplyComment}
			/>

			{status && (
				<InfoKeyValue
					className="key-value-container"
					label={t('table.requestStatus')}
				>
					<ProductStatusChip isCustomer={isCustomer} status={status} />
				</InfoKeyValue>
			)}

			<InfoKeyValue
				className="key-value-container"
				label={t('formField.declinedReasonLabel')}
				value={reason}
			/>
		</S.DetailsContainer>
	)
}

export { DetailsHeader }
